import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import { getTranslation } from './translations'

const HTML_ENTITY_MAP = {
  amp: '&',
  apos: '\'',
  gt: '>',
  lt: '<',
  quot: '"',
}

export const decodeHtmlEntities = value =>
  String(value || '').replace(
    /&(#x[\da-f]+|#\d+|amp|apos|gt|lt|quot);/gi,
    (entity, name) => {
      const normalizedName = name.toLowerCase()
      if (normalizedName.startsWith('#x')) {
        return String.fromCodePoint(parseInt(normalizedName.slice(2), 16))
      }
      if (normalizedName.startsWith('#')) {
        return String.fromCodePoint(parseInt(normalizedName.slice(1), 10))
      }
      return HTML_ENTITY_MAP[normalizedName] || entity
    }
  )

export const calculateEstimatedForm = (player, playerSkills) => {
  const displayedTsi = Number(player?.tsi)
  const age = Number(player?.age)
  const ageMultiplier =
    Number.isFinite(age) && age >= 28 ? 0.85 ** (age - 27) : 1
  const tsi = displayedTsi / ageMultiplier
  const stamina = Number(playerSkills?.staminaSkill)
  const defending = Number(playerSkills?.defenderSkill)
  const playmaking = Number(playerSkills?.playmakerSkill)
  const scoring = Number(playerSkills?.scorerSkill)
  const passing = Number(playerSkills?.passingSkill)
  const winger = Number(playerSkills?.wingerSkill)
  const keeper = Number(playerSkills?.keeperSkill)

  const values = [
    displayedTsi,
    ageMultiplier,
    stamina,
    defending,
    playmaking,
    scoring,
    passing,
    winger,
    keeper,
  ]
  if (!values.every(Number.isFinite) || tsi <= 0) return null

  const isGoalkeeper =
    keeper > 0 &&
    keeper >= Math.max(defending, playmaking, scoring, passing, winger)

  if (isGoalkeeper) {
    if (keeper <= 1) return null
    return (tsi / (3 * (keeper - 1) ** 3.359)) ** 2
  }

  if (stamina <= 1) return null

  const skillFactor =
    1.03 * (defending - 1) ** 3 +
    1.03 * (playmaking - 1) ** 3 +
    1.03 * (scoring - 1) ** 3 +
    (passing - 1) ** 3 +
    0.84 * (winger - 1) ** 3

  if (skillFactor <= 0) return null

  return (tsi * 1000 / (skillFactor ** 2 * Math.sqrt(stamina - 1))) ** 2
}

/**
 * "Takaisin listoihin" -nappi, uudelleenkäytettävä komponentti.
 */
export const BackToListsButton = () => {
  const navigate = useNavigate()
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)

  return (
    <Button
      variant="outline-light"
      size="sm"
      onClick={() => navigate('/lists')}
    >
      ← {t('backToLists')}
    </Button>
  )
}

/**
 * Tarkistaa onko pelaajan päivitys vanha.
 * Palauttaa true, jos dateUpdated on ennen viimeisintä perjantaita klo 6:00.
 * Jos tänään on perjantai, käytetään tämän päivän perjantaita.
 *
 * @param {string|Date} dateString - Päivityksen päivämäärä
 * @returns {boolean} - True jos päivitys on vanha
 */
export const isOld = dateString => {
  if (!dateString) return true

  const updatedDate = new Date(dateString)
  const now = new Date()

  // Laske viimeisin perjantai klo 6:00
  const dayOfWeek = now.getDay() // 0=su, 1=ma, ..., 5=pe, 6=la
  let daysSinceFriday

  if (dayOfWeek === 5) {
    // Tänään on perjantai → käytetään tätä päivää
    daysSinceFriday = 0
  } else if (dayOfWeek === 6) {
    // Lauantai → eilen oli perjantai
    daysSinceFriday = 1
  } else {
    // Su=0 → 2, Ma=1 → 3, Ti=2 → 4, Ke=3 → 5, To=4 → 6
    daysSinceFriday = dayOfWeek + 2
  }

  const lastFriday = new Date(now)
  lastFriday.setDate(now.getDate() - daysSinceFriday)
  lastFriday.setHours(6, 0, 0, 0) // Klo 6:00

  return updatedDate < lastFriday
}

/**
 * Tarkistaa onko pelaajan päivitys yli kuukauden vanha.
 *
 * @param {string|Date} dateString - Päivityksen päivämäärä
 * @returns {boolean} - True jos yli 30 päivää vanha
 */
export const isOverMonthOld = dateString => {
  if (!dateString) return true

  const updatedDate = new Date(dateString)
  const oneMonthAgo = new Date()
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 30)

  return updatedDate < oneMonthAgo
}

/**
 * Laskee pelaajan todellisen iän huomioiden päivityspäivän ja nykyhetken erotuksen.
 * Hattrickissä 1 päivä = 1 oikea päivä, mutta 1 vuosi = 112 päivää.
 *
 * @param {Object} player - Pelaajaobjekti jossa age, ageDays ja dateUpdated
 * @returns {{ years: number, days: number }} - Todellinen ikä
 */
export const calculateActualAge = player => {
  if (!player || player.age === undefined) {
    return { years: 0, days: 0 }
  }

  if (!player.dateUpdated) {
    return { years: player.age, days: player.ageDays || 0 }
  }

  const now = new Date()
  const updated = new Date(player.dateUpdated)
  const daysPassed = Math.floor((now - updated) / (1000 * 60 * 60 * 24))

  let totalDays = (player.ageDays || 0) + daysPassed
  let totalYears = player.age

  while (totalDays >= 112) {
    totalDays -= 112
    totalYears += 1
  }

  return { years: totalYears, days: totalDays }
}

export const dateFin = dateStr => {
  const date = new Date(dateStr)
  const formattedDate = date.toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  return formattedDate
}

/**
 * Valmistele harjoitussuunnitelman data backendille lähetettäväksi.
 * Käytetään ScoutSkills.js:ssä ja muualla missä luodaan treenisuunnitelmia.
 *
 * @param {Object} player - Pelaajaobjekti
 * @param {Object} playerSkills - Pelaajan nykyiset taidot
 * @param {Array} targetSkills - Tavoitetaidot [{skill, target}]
 * @returns {Object} - Valmis data API-kutsulle
 */
export const prepareTrainingPlanData = (player, playerSkills, targetSkills) => {
  return {
    id: player.playerId,
    currentSkills: playerSkills,
    trainingData: {
      coach: player.trainingData.coach,
      assistants: player.trainingData.assistants,
      intensity: player.trainingData.intensitety,
      stamina: player.trainingData.stamina,
      mode: player.trainingData.mode,
      minutes: 90,
      efficiency: 1,
    },
    age: [player.age, player.ageDays],
    targetSkills: targetSkills.map(({ skill, target }) => ({
      skill,
      target: Number(target),
    })),
    date: new Date(),
  }
}

/**
 * Rakennetaan tavoitetaidot tavoitetasoista tietylle pelaajatyypille.
 *
 * @param {Object} targetLevelSkills - Tavoitetasojen taitodata yhdelle pelaajatyypille
 * @returns {Array} - [{skill, target}] taulukko non-null taidoista
 */
export const buildTargetSkillsFromLevel = targetLevelSkills => {
  const skillKeys = [
    'keeperSkill',
    'defenderSkill',
    'playmakerSkill',
    'wingerSkill',
    'passingSkill',
    'scorerSkill',
    'setPiecesSkill',
  ]
  const targetSkills = []
  for (const key of skillKeys) {
    const value = targetLevelSkills[key]
    if (value !== null && value !== undefined) {
      targetSkills.push({ skill: key, target: value })
    }
  }
  return targetSkills
}
